import { bunja } from 'bunja';
import { atom } from 'jotai';
import { ValidationError } from 'yup';

import { PutUserProfileParams, SocialType } from '@flipster/apis/v1/user/types';
import { overlayItemBunja } from '@flipster/overlay';
import { snackbar } from '@flipster/react/design-system/snackbar/index';
import { createStepComponent } from '@flipster/react/misc/step/Steps';
import { createStepsAtom } from '@flipster/react/misc/step/atom';
import { alert } from '@flipster/utils/alert';
import { getDetailedErrorResponse } from '@flipster/utils/axios';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { formatDatetime } from '@flipster/utils/datetime';
import { EnvScope } from '@flipster/utils/env';
import { getErrorCode } from '@flipster/utils/error';
import { deleteLocalStorage, getLocalStorage, setLocalStorage } from '@flipster/utils/localStorage';
import { map } from '@flipster/utils/map';
import segment from '@flipster/utils/segment';
import { CommunityEvent } from '@flipster/utils/segment/events/community';
import { ProfileEvent } from '@flipster/utils/segment/events/profile';

import { profileSettingBunja } from '../../shared/community/profile';
import { profileBunja } from '../bunja';
import { visibilityToBoolean } from '../misc';
import { oauthProviderSearchParamKey } from './shared/oauth';
import { UsernameValidationMessage, usernameSchema } from './username-schema';

const socialLinkProviders: { label: string; provider: SocialType }[] = [
  {
    label: 'Telegram',
    provider: 'TELEGRAM',
  },
  // legacy, only for users who already register their twitter link.
  {
    label: 'Twitter (X)',
    provider: 'X',
  },
];

export const socialLinkDomains: Record<SocialType, string> = {
  X: 'https://x.com/',
  TELEGRAM: 'https://t.me/',
};

interface ValidationResult {
  valid: boolean;
  message?: string;
}

type EditProfileModel = {
  form: undefined;
  public: { secondaryLabel: string; onSecondary: () => void };
};

export const editProfileBunja = bunja(() => {
  const env = bunja.use(EnvScope);
  const store = bunja.use(JotaiStoreScope);
  const { profileAtom } = bunja.use(profileBunja);

  // step
  const Steps = createStepComponent<EditProfileModel>();
  const steps = createStepsAtom<EditProfileModel>({ step: 'form', context: undefined });

  // username
  const baseUsernameAtom = atom((get) => get(profileAtom)?.nickname);
  const edittingUsernameAtom = atom<string | undefined>(undefined);
  const usernameAtom = atom(
    (get) => get(edittingUsernameAtom) ?? get(baseUsernameAtom),
    (_, set, value: string) => {
      set(edittingUsernameAtom, value);
      set(usernameServerValidationMessageAtom, undefined);
    },
  );
  const usernamePristineAtom = atom(
    (get) => get(edittingUsernameAtom) == null || get(edittingUsernameAtom) == get(baseUsernameAtom),
  );
  const usernameValidationAtom = atom<ValidationResult>((get) => {
    if (get(usernamePristineAtom)) return { valid: true };

    const message = get(usernameServerValidationMessageAtom);
    if (message) return { valid: false, message };

    const edittingUsername = get(edittingUsernameAtom);
    try {
      usernameSchema.validateSync(edittingUsername);
    } catch (e) {
      if (e instanceof ValidationError) {
        return { valid: false, message: UsernameValidationMessage[e.message] };
      }
      return { valid: false };
    }
    return { valid: true };
  });
  const usernameServerValidationMessageAtom = atom<string | undefined>(undefined);

  // social links
  const baseSocialLinksAtom = atom((get) => {
    const socials = get(profileAtom)?.socialProfiles;
    return {
      X: findAndStrip('X'),
      TELEGRAM: findAndStrip('TELEGRAM'),
    };
    function findAndStrip(type: SocialType) {
      return map(
        socials?.find(({ socialType }) => socialType === type),
        ({ profileUrl }) => strip(type, profileUrl),
      );
    }
  });
  const edittingSocialLinksAtom = atom<Record<SocialType, string | undefined>>({ X: undefined, TELEGRAM: undefined });
  const socialLinksWithDomainAtom = atom<Record<SocialType, string>>((get) => ({
    X: `${socialLinkDomains.X}${(get(edittingSocialLinksAtom).X ?? get(baseSocialLinksAtom).X) || ''}`,
    TELEGRAM: `${socialLinkDomains.TELEGRAM}${(get(edittingSocialLinksAtom).TELEGRAM ?? get(baseSocialLinksAtom).TELEGRAM) || ''}`,
  }));
  const strippedSocialLinksAtom = atom<Record<SocialType, string>>((get) => ({
    X: strip('X', get(socialLinksWithDomainAtom).X),
    TELEGRAM: strip('TELEGRAM', get(socialLinksWithDomainAtom).TELEGRAM),
  }));
  const socialLinksAtom = atom<Record<SocialType, string>, [SocialType, string], void>(
    (get) => get(socialLinksWithDomainAtom),
    (get, set, type: SocialType, value: string) => {
      set(edittingSocialLinksAtom, { ...get(edittingSocialLinksAtom), [type]: strip(type, value) });
    },
  );
  const socialLinksPristineAtom = atom((get) => ({
    X: get(edittingSocialLinksAtom).X == null || get(edittingSocialLinksAtom).X == get(baseSocialLinksAtom).X,
    TELEGRAM:
      get(edittingSocialLinksAtom).TELEGRAM == null ||
      get(edittingSocialLinksAtom).TELEGRAM == get(baseSocialLinksAtom).TELEGRAM,
  }));
  const socialLinksValidationAtom = atom((get) => ({
    X: validateLink(get(socialLinksWithDomainAtom).X),
    TELEGRAM: validateLink(get(socialLinksWithDomainAtom).TELEGRAM),
  }));

  // visibility
  const visibilityAtom = atom((get) => get(profileAtom)?.mySettings);
  const showBothToPublicSectionAtom = atom((get) => {
    const visibility = get(visibilityAtom);
    if (!visibility || get(profileAtom)?.socialProfiles.length == 0) return false;
    return [visibility.tradeHistoryVisibility, visibility.performanceVisibility]
      .map(visibilityToBoolean)
      .some((v) => !v);
  });

  // form status
  const pristineAtom = atom((get) =>
    [get(usernamePristineAtom), ...Object.values(get(socialLinksPristineAtom))].every(Boolean),
  );
  const validationAtom = atom((get) =>
    [get(usernameValidationAtom).valid, ...Object.values(get(socialLinksValidationAtom))].every(Boolean),
  );
  const payloadAtom = atom<PutUserProfileParams>((get) => {
    const payload: PutUserProfileParams = {};
    if (!get(usernamePristineAtom)) payload.nickname = get(edittingUsernameAtom);
    for (const provider of Object.keys(socialLinkDomains) as SocialType[]) {
      if (!get(socialLinksPristineAtom)[provider]) {
        // provide empty string if user wants to remove social link
        const profileUrl = get(edittingSocialLinksAtom)[provider] ? get(socialLinksWithDomainAtom)[provider] : '';
        (payload.socialProfiles ??= []).push({ socialType: provider, profileUrl });
      }
    }
    // make performance/history visibility public if socialProfiles exists
    if (payload.socialProfiles) {
      payload.settings = {
        performanceVisibility: 'PUBLIC',
        tradeHistoryVisibility: 'PUBLIC',
      };
    }
    return payload;
  });
  const needPublicAgreementAtom = atom((get) => {
    if (!get(payloadAtom).socialProfiles) return false;
    const { performanceVisibility, tradeHistoryVisibility } = get(profileAtom)?.mySettings ?? {};
    return [performanceVisibility, tradeHistoryVisibility].some((visibility) => visibility === 'PRIVATE');
  });

  // x oauth
  const socialConnectionBaseAtom = atom((get) => get(profileAtom)?.socialConnections ?? []);
  const xConnectionAtom = atom((get) =>
    get(socialConnectionBaseAtom).find((connection) => connection.socialName == 'X'),
  );
  const socialLinkProvidersAtom = atom((get) => {
    if (get(xConnectionAtom) || get(baseSocialLinksAtom).X == null) {
      return socialLinkProviders.filter((item) => item.provider != 'X');
    }
    return socialLinkProviders;
  });

  // save-and-load
  const draftKey = `flipster.configs.v1.${env.NEXT_PUBLIC_VERSION}.editProfileDraftForOAuth` as const;
  function saveDraft() {
    if (store.get(pristineAtom)) return;
    const username = store.get(edittingUsernameAtom);
    const socialLinks = store.get(edittingSocialLinksAtom);
    setLocalStorage(draftKey, { username, socialLinks });
  }
  function loadAndClearDraft() {
    const draft = getLocalStorage(draftKey);
    if (!draft) return;
    try {
      const d = draft as unknown as { username: string; socialLinks: Record<SocialType, string | undefined> };
      store.set(edittingUsernameAtom, d.username);
      store.set(edittingSocialLinksAtom, d.socialLinks);
    } finally {
      deleteLocalStorage(draftKey);
    }
  }

  bunja.effect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.get(oauthProviderSearchParamKey)) return;
    // restore if there is stored draft
    loadAndClearDraft();
    // remaining oauth step and cleanup will be performed at ./social/bunja.tsx
  });

  const initialSocialConnectionSegmentAtom = atom((get) => {
    const socialLinks = get(baseSocialLinksAtom);
    return { connected_x: socialLinks.X != null, telegram: socialLinks.TELEGRAM };
  });
  const initialSocialProvidersSegmentAtom = atom((get) => {
    const socialLinks = get(baseSocialLinksAtom);
    return { x: socialLinks.X, telegram: socialLinks.TELEGRAM };
  });

  return {
    // step
    Steps,
    steps,
    // form
    usernameAtom,
    usernamePristineAtom,
    usernameValidationAtom,
    usernameServerValidationMessageAtom,
    edittingUsernameAtom,
    socialLinkProvidersAtom,
    socialLinksAtom,
    strippedSocialLinksAtom,
    socialLinksPristineAtom,
    socialLinksValidationAtom,
    edittingSocialLinksAtom,
    showBothToPublicSectionAtom,
    pristineAtom,
    validationAtom,
    payloadAtom,
    needPublicAgreementAtom,
    // oauth
    xConnectionAtom,
    // save-and-load
    saveDraft,
    // segment
    initialSocialConnectionSegmentAtom,
    initialSocialProvidersSegmentAtom,
  };
});

export const editProfileActionBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { close } = bunja.use(overlayItemBunja);
  const {
    steps,
    usernameAtom,
    edittingUsernameAtom,
    usernamePristineAtom,
    needPublicAgreementAtom,
    usernameServerValidationMessageAtom,
    socialLinksAtom,
    payloadAtom,
  } = bunja.use(editProfileBunja);
  const { putUserProfileMutationAtom } = bunja.use(profileSettingBunja);

  function closeAndShowUpdatedToast() {
    snackbar.success({ message: 'Your profile has been updated' });
    close();
  }
  const mutateProfile = async (request: PutUserProfileParams) => {
    try {
      const { mutateAsync } = store.get(putUserProfileMutationAtom);
      await mutateAsync(request);
      closeAndShowUpdatedToast();
    } catch (error) {
      const errorCode = getErrorCode(error);
      switch (errorCode) {
        case 'api.user.profile.invalidNickname':
        case 'api.user.profile.duplicatedNickname':
          store.set(usernameServerValidationMessageAtom, UsernameValidationMessage[errorCode]);
          break;
        case 'api.user.profile.nicknameUpdateBlocked': {
          const detailedError = getDetailedErrorResponse<{ nicknameUpdateAvailableAt: string }>(error);
          if (!detailedError) break;
          segment.track(CommunityEvent.UsernameChangeErrorView);
          store.set(
            usernameServerValidationMessageAtom,
            `Username changes are limited to once every 30 days. Next update: ${formatDatetime(detailedError.errorDetails.nicknameUpdateAvailableAt, { format: 'MMM dd, yyyy, HH:mm zz', isUTC: true })}`,
          );
          break;
        }
        default:
          break;
      }
      store.set(steps.resetStepAtom);
    }
  };

  async function saveProfile() {
    const username = store.get(usernameAtom) ?? '';
    const socialLinks = store.get(socialLinksAtom);
    segment.track(ProfileEvent.EditProfileSaveClick, {
      username,
      telegram: socialLinks.TELEGRAM,
      connected_x: socialLinks.X != null,
    });
    if (store.get(needPublicAgreementAtom)) {
      return store.set(steps.pushAtom, {
        step: 'public',
        context: store.get(usernamePristineAtom)
          ? { secondaryLabel: 'Close', onSecondary: () => store.set(steps.backAtom) }
          : {
              secondaryLabel: 'Save username only',
              onSecondary: async () => {
                try {
                  await mutateProfile({ nickname: store.get(edittingUsernameAtom) });
                } catch {
                  // Do nothing
                }
              },
            },
      });
    }
    try {
      await mutateProfile(store.get(payloadAtom));
    } catch {
      // Do nothing
    }
  }

  async function changeBothVisibilityToPublic() {
    try {
      await mutateProfile({ settings: { performanceVisibility: 'PUBLIC', tradeHistoryVisibility: 'PUBLIC' } });
      alert.show({
        title: 'Your performance and trading activity are now public',
        message: 'Other users can now view your performance and trading activity.',
        confirmText: 'Done',
      });
    } catch {
      // Do nothing
    }
  }

  return {
    mutateProfile,
    saveProfile,
    changeBothVisibilityToPublic,
  };
});

function strip(type: SocialType, value: string) {
  const placeholder = socialLinkDomains[type];
  const stripped = value.slice(placeholder.length);
  try {
    // for copy-pasting whole links
    return new URL(stripped).pathname.slice(1);
  } catch {
    return stripped;
  }
}

function validateLink(link: string) {
  try {
    const url = new URL(link);
    // i know this is valid url but spec wants this;
    if (/\/\/+/.test(url.pathname)) return false;
    return Boolean(new URL(link));
  } catch {
    return false;
  }
}
